import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash2, ChevronsUpDown, Check, ReceiptText, Receipt, CreditCard, Wallet, Banknote, Smartphone } from "lucide-react";
import { format } from "date-fns";
import { formatINR } from "@/lib/currency";

const PAYMENT_METHODS = [
  { key: "UPI", icon: Smartphone },
  { key: "CASH", icon: Banknote },
  { key: "CARD", icon: CreditCard },
  { key: "WALLET", icon: Wallet },
];

const idOf = (x) => String(x?.id ?? x?._id ?? x?.productId ?? x?.customerId ?? "");
const emptyProductRow = { productId: "", name: "", staffId: "", staffName: "", qty: 1, price: 0 };

export default function ProductSale() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [productRows, setProductRows] = useState([{ ...emptyProductRow }]);
  const [productOpenIdx, setProductOpenIdx] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [payments, setPayments] = useState([{ method: "UPI", amount: 0, reference: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);

  const addPayment = () => setPayments([...payments, { method: "CASH", amount: 0, reference: "" }]);
  const updatePayment = (i, patch) => { const n = [...payments]; n[i] = { ...n[i], ...patch }; setPayments(n); };
  const removePayment = (i) => setPayments(payments.length > 1 ? payments.filter((_, idx) => idx !== i) : payments);
  const needsRef = (m) => m === "UPI" || m === "CARD";

  const displayName = (s) =>
    s?.name || s?.fullName || [s?.firstName, s?.lastName].filter(Boolean).join(" ") || "Unnamed";

  useEffect(() => {
    (async () => {
      try {
        const [c, st, p] = await Promise.all([
          api.get("/customers"), api.get("/staff/bookable"), api.get("/products"),
        ]);
        setCustomers(c.data); setStaff(st.data); setProducts(p.data);
      } catch (e) {
        const data = e.response?.data;
        toast.error(data?.detail || data?.message || "Failed to load data");
      }
    })();
  }, []);

  const updateProductRow = (i, patch) => {
    const n = [...productRows];
    n[i] = { ...n[i], ...patch };
    setProductRows(n);
  };
  const pickProduct = (i, pid) => {
    const prod = products.find((p) => idOf(p) === String(pid));
    if (prod) updateProductRow(i, { productId: idOf(prod), name: prod.name, price: prod.sellingPrice || prod.price || 0 });
  };
  const pickProductStaff = (i, sid) => {
    const s = staff.find((x) => idOf(x) === String(sid));
    if (s) updateProductRow(i, { staffId: idOf(s), staffName: displayName(s) });
  };
  const addProductRow = () => setProductRows([...productRows, { ...emptyProductRow }]);
  const removeProductRow = (i) => setProductRows(productRows.length > 1 ? productRows.filter((_, idx) => idx !== i) : productRows);
  const productRowTotal = (r) => Number(r.qty) * Number(r.price);

  const subtotal = productRows.reduce((s, r) => s + productRowTotal(r), 0);
  const grandTotal = subtotal - Number(discount || 0);
  const paidTotal = payments.reduce((s, p, i) => s + Number(p.amount || (i === 0 && payments.length === 1 ? grandTotal : 0)), 0);

  const selectedCustomer = customers.find((c) => idOf(c) === String(customerId));

  useEffect(() => {
    const customer = customers.find((c) => idOf(c) === String(customerId));
    if (customer?.membershipPlanName && Number(customer?.walletBalance || 0) > 0) {
      setPayments([{ method: "WALLET", amount: 0, reference: "" }]);
    } else {
      setPayments([{ method: "UPI", amount: 0, reference: "" }]);
    }
  }, [customerId, customers]);

  const submit = async () => {
    setTriedSubmit(true);
    if (!customerId) { toast.error("Select a customer"); return; }
    if (productRows.some((r) => !r.productId)) { toast.error("Each row needs a product"); return; }
    if (productRows.some((r) => !r.staffId)) { toast.error("Each row needs a staff member"); return; }
    if (Math.abs(paidTotal - grandTotal) > 0.01) { toast.error(`Payments total (${formatINR(paidTotal)}) must equal Grand Total (${formatINR(grandTotal)})`); return; }
    for (const p of payments) {
      if (needsRef(p.method) && !String(p.reference || "").trim()) {
        toast.error(`Reference number required for ${p.method}`); return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/invoices/product-sale", {
        customerId: customerId,
        invoiceDate: date + "T" + new Date().toTimeString().slice(0, 8),
        productItems: productRows.filter((r) => r.productId).map((r) => ({
          productId: r.productId,
          staffId: r.staffId,
          quantity: Number(r.qty),
          unitPrice: Number(r.price),
        })),
        totalAmount: grandTotal,
        discountAmount: Number(discount || 0),
        payments: payments.map((p, i) => ({
          paymentMethod: p.method,
          amount: Number(p.amount) || (i === 0 && payments.length === 1 ? grandTotal : 0),
          referenceNumber: p.reference || null,
        })),
      });
      toast.success("Product Sale Recorded");
      nav("/dashboard/invoices");
    } catch (e) {
      const data = e.response?.data;
      toast.error(data?.detail || data?.message || data?.error || "Failed to record sale");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-8" data-testid="product-sale-page">
      <div>
        <div className="overline mb-2">Point of Sale</div>
        <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3"><Receipt className="w-8 h-8 text-emerald-500" /> Product Sale</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-3">Customer <span className="text-red-500">*</span></div>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="ps-customer-trigger"
                    className={`w-full h-12 flex items-center justify-between bg-emerald-elev border rounded-md px-3 text-white hover:border-gold ${triedSubmit && !customerId ? "border-red-500" : "border-gold-subtle"}`}>
                    <span className={customerId ? "text-white" : "text-emerald-muted"}>
                      {selectedCustomer ? `${selectedCustomer.firstName + " " + (selectedCustomer.lastName || "")} — ${selectedCustomer.phone || selectedCustomer.email}` : "Search by name or phone…"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-emerald-muted opacity-70" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] bg-emerald-surface border-gold-subtle">
                  <Command className="bg-emerald-surface text-white"
                    filter={(v, s) => (v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
                    <CommandInput placeholder="Type name, email, or phone…" className="text-white" />
                    <CommandList>
                      <CommandEmpty className="py-4 text-center text-sm text-emerald-muted">No customers found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((c) => (
                          <CommandItem key={idOf(c)} value={`${c.firstName || ""} ${c.lastName || ""} ${c.phone || ""} ${c.email || ""}`}
                            onSelect={() => { setCustomerId(idOf(c)); setCustomerOpen(false); }}
                            className="text-white cursor-pointer aria-selected:bg-[rgba(197,160,89,0.15)] aria-selected:text-gold">
                            <Check className={`mr-2 h-4 w-4 ${customerId === idOf(c) ? "opacity-100 text-gold" : "opacity-0"}`} />
                            <div className="flex flex-col">
                              <span className="text-sm">{c.firstName} {c.lastName || ""}</span>
                              <span className="text-xs text-emerald-muted">{c.phone ? `${c.phone} · ` : ""}{c.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedCustomer?.membershipPlanName && Number(selectedCustomer?.walletBalance || 0) > 0 && (
                <div className="mt-2 flex items-center gap-2 text-sm bg-emerald-elev/60 border border-gold-subtle rounded-md px-3 py-2">
                  <Wallet className="w-4 h-4 text-gold" />
                  <span className="text-emerald-muted">Wallet Balance:</span>
                  <span className="text-gold font-medium">{formatINR(selectedCustomer.walletBalance)}</span>
                </div>
              )}
            </div>
            <div>
              <div className="overline mb-3">Date</div>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="bg-emerald-elev border-gold-subtle text-white h-12" />
            </div>
          </div>

          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Products</div>
              <button onClick={addProductRow} className="gold-outline-button px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add product
              </button>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-2 text-xs text-emerald-muted uppercase tracking-widest px-2 pb-2 border-b border-gold-subtle">
              <div className="col-span-4">Product <span className="text-red-500">*</span></div>
              <div className="col-span-2">Staff <span className="text-red-500">*</span></div>
              <div className="col-span-1 text-center">Qty</div>
              <div className="col-span-2 text-right">Price</div>
              <div className="col-span-2 text-right">Total</div>
              <div className="col-span-1"></div>
            </div>
            <div className="space-y-3 mt-3">
              {productRows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12 md:col-span-4">
                    <Popover open={productOpenIdx === i} onOpenChange={(open) => setProductOpenIdx(open ? i : null)}>
                      <PopoverTrigger asChild>
                        <button type="button"
                          className={`w-full h-10 flex items-center justify-between bg-emerald-elev border rounded-md px-3 text-white hover:border-gold ${triedSubmit && !r.productId ? "border-red-500" : "border-gold-subtle"}`}>
                          <span className={r.productId ? "text-white truncate" : "text-emerald-muted"}>
                            {r.productId ? r.name : "Search product…"}
                          </span>
                          <ChevronsUpDown className="w-4 h-4 text-emerald-muted opacity-70 shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] bg-emerald-surface border-gold-subtle">
                        <Command className="bg-emerald-surface text-white" filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                          <CommandInput placeholder="Type product name…" className="text-white" />
                          <CommandList>
                            <CommandEmpty className="py-4 text-center text-sm text-emerald-muted">No products found.</CommandEmpty>
                            <CommandGroup>
                              {products.map((p) => (
                                <CommandItem key={idOf(p)} value={`${p.name} ${p.brand || ""}`}
                                  onSelect={() => { pickProduct(i, idOf(p)); setProductOpenIdx(null); }}
                                  className="text-white cursor-pointer aria-selected:bg-[rgba(197,160,89,0.15)] aria-selected:text-gold">
                                  <Check className={`mr-2 h-4 w-4 ${r.productId === idOf(p) ? "opacity-100 text-gold" : "opacity-0"}`} />
                                  <div className="flex flex-col">
                                    <span className="text-sm">{p.name}</span>
                                    <span className="text-xs text-emerald-muted">{formatINR(p.sellingPrice || p.price || 0)}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-12 md:col-span-2">
                    <Select value={r.staffId} onValueChange={(v) => pickProductStaff(i, v)}>
                      <SelectTrigger className={`bg-emerald-elev text-white h-10 ${triedSubmit && !r.staffId ? "border-red-500" : "border-gold-subtle"}`}><SelectValue placeholder="Staff" /></SelectTrigger>
                      <SelectContent className="bg-emerald-surface border-gold-subtle text-white">
                        {staff.map((s) => <SelectItem key={idOf(s)} value={idOf(s)}>{displayName(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3 md:col-span-1">
                    <Input type="number" min="1" value={r.qty} onChange={(e) => updateProductRow(i, { qty: e.target.value })}
                      className="bg-emerald-elev border-gold-subtle text-white text-center h-10" />
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <Input type="number" step="0.01" value={r.price} onChange={(e) => updateProductRow(i, { price: e.target.value })}
                      className="bg-emerald-elev border-gold-subtle text-white text-right h-10" />
                  </div>
                  <div className="col-span-2 md:col-span-2 text-right text-gold font-medium">{formatINR(productRowTotal(r))}</div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => removeProductRow(i)} className="text-emerald-muted hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Billing summary + Payment */}
        <div className="space-y-6">
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline mb-4 flex items-center gap-2"><ReceiptText className="w-4 h-4 text-gold" /> Billing Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-white"><span>Subtotal</span><span className="text-white">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between items-center text-white">
                <span>Discount (₹)</span>
                <Input type="number" step="1.00" value={discount} onChange={(e) => setDiscount(e.target.value)}
                  className="w-28 bg-emerald-elev border-gold-subtle text-white text-right h-8 text-sm" />
              </div>
              <div className="border-t border-gold-subtle mt-3 pt-3 flex justify-between items-baseline">
                <span className="text-white">Grand Total</span>
                <span className="text-xl font-bold text-gold tabular-nums">{formatINR(grandTotal)}</span>
              </div>
            </div>
          </div>

          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Payments</div>
              <button onClick={addPayment} className="gold-outline-button px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Split</button>
            </div>
            <div className="space-y-3">
              {payments.map((p, i) => (
                <div key={i} className="space-y-2 pb-3 border-b border-gold-subtle last:border-0">
                  <div className="grid grid-cols-4 gap-2">
                    {PAYMENT_METHODS.map(({ key, icon: Icon }) => (
                      <button key={key} onClick={() => updatePayment(i, { method: key })}
                        className={`flex items-center justify-center gap-1 py-2 rounded-md border text-xs transition ${p.method === key ? "bg-[rgba(197,160,89,0.15)] border-gold text-gold" : "bg-emerald-elev border-gold-subtle text-emerald-muted hover:text-gold"}`}>
                        <Icon className="w-3 h-3" /> {key}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <Input type="number" step="0.01" placeholder="Amount" value={p.amount || (i === 0 && payments.length === 1 ? grandTotal.toFixed(2) : "")} onChange={(e) => updatePayment(i, { amount: e.target.value })}
                      className="col-span-6 bg-emerald-elev border-gold-subtle text-white h-10" />
                    <Input placeholder={needsRef(p.method) ? "Reference No." : "Reference (optional)"} value={p.reference} onChange={(e) => updatePayment(i, { reference: e.target.value })}
                      className="col-span-5 bg-emerald-elev border-gold-subtle text-white h-10" />
                    <button onClick={() => removePayment(i)} className="col-span-1 text-emerald-muted hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="pt-3 border-t border-gold-subtle flex justify-between text-sm">
            <span className="text-emerald-muted">Paid</span>
            <span className={`font-medium ${Math.abs(paidTotal - grandTotal) < 0.01 ? "text-gold" : "text-red-300"}`}>{formatINR(paidTotal)} / {formatINR(grandTotal)}</span>
          </div>

          <button onClick={submit} disabled={submitting}
            className="gold-button w-full h-14 rounded-md disabled:opacity-60">
            {submitting ? "Recording…" : `Charge ${formatINR(grandTotal)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
